import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setTitle } from "../../../helpers/MetaTag";
import { resetStep } from "../../../redux/slice/stepSlice";
import { clearSketch } from "../../../redux/slice/sketchSlice";
import Button from "../../../components/ui/Button";
import BottomBar from "../../../components/ui/BottomBar";
import WizardShell from "../../../components/ui/WizardShell";
import ErrorState from "../../../components/ui/ErrorState";
import Skeleton, { SkeletonGroup } from "../../../components/ui/Skeleton";
import AddressCard from "../../../components/gift/AddressCard";
import { getGift, giftIdFromSearch, giftPagePath, giftPlanPath, newGiftBookPath } from "../../../services/giftService";

/**
 * /gift/welcome?id=... Gift 1 (docs/design/HANDOFF.md): who it's from, what
 * it includes, the giver's message, the mailing address with Edit, and
 * "Start my book". One book goes straight to the builder; more go to the
 * split screen first.
 */
const GiftWelcome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const giftId = giftIdFromSearch(location.search, "id");
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTitle("Your gift | MemoryDoodle");
  }, []);

  const load = async () => {
    if (!giftId) return setError("We couldn't find your gift.");
    setError(null);
    const res = await getGift(giftId);
    if (res?.success) setGift(res.data);
    else setError(res?.message || "We couldn't load your gift.");
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftId]);

  // A recipient who already started books (or whose gift is closed or sent) belongs on the hub.
  useEffect(() => {
    if (gift && (!gift.open || gift.submitted || gift.books.length)) navigate(giftPagePath(gift.giftId), { replace: true });
  }, [gift, navigate]);

  const start = () => {
    if (!gift) return;
    if (gift.quantity > 1) {
      navigate(giftPlanPath(gift.giftId));
      return;
    }
    dispatch(resetStep());
    dispatch(clearSketch());
    navigate(newGiftBookPath(gift.giftId));
  };

  if (error) {
    return (
      <WizardShell plain>
        <ErrorState title="We couldn't open your gift" onRetry={load}>
          {error}
        </ErrorState>
      </WizardShell>
    );
  }
  if (!gift) {
    return (
      <WizardShell plain>
        <SkeletonGroup label="Loading your gift" className="flex flex-col gap-4">
          <Skeleton shape="line" className="h-9 w-2/3" />
          <Skeleton shape="block" className="h-24" />
          <Skeleton shape="block" className="h-32" />
        </SkeletonGroup>
      </WizardShell>
    );
  }
  if (!gift.open || gift.submitted || gift.books.length) return null;

  const giver = gift.giftFrom || "Someone special";
  const n = gift.quantity;
  const words = { 2: "both", 3: "all three", 4: "all four", 5: "all five" }[n] || "all of them";
  const primary = (
    <Button size="lg" fullWidth onClick={start}>
      Start my book
    </Button>
  );

  return (
    <WizardShell plain bottomBar={<BottomBar primary={primary} />}>
      <div className="flex flex-col gap-4 lg:mx-auto lg:max-w-[640px]">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Your gift from {giver}</h1>
          <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">
            {n > 1
              ? `It includes ${n} printed coloring books made from your photos. Make one book for ${words}, or a different book for each.`
              : "It's a printed coloring book made from your own photos."}
          </p>
        </div>

        {gift.giftMessage ? (
          <figure className="m-0 rounded-[14px] border border-md-pink-light-1 bg-white px-4 py-3.5">
            <blockquote className="m-0 whitespace-pre-line text-[15px] italic leading-relaxed text-md-dark md:text-base">{gift.giftMessage}</blockquote>
            <figcaption className="mt-2 text-[13px] font-semibold text-md-muted">From {giver}</figcaption>
          </figure>
        ) : null}

        <div className="hidden lg:block">{primary}</div>

        {gift.addressNeeded ? (
          <AddressCard giftId={gift.giftId} address={gift.address} single={n === 1} note={`${giver} added this address. Change it if it's not right.`} onSaved={(data) => setGift(data)} />
        ) : null}
      </div>
    </WizardShell>
  );
};

export default GiftWelcome;
